import { type Table as TanStackTable, type Column } from '@tanstack/react-table'
import { Settings2, RotateCcw, Columns, MoveHorizontal } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface ColumnVisibilityMenuProps<TData> {
  /** Instancia de tabla TanStack */
  table: TanStackTable<TData>
  /** Callback para restaurar visibilidad a valores por defecto */
  onResetVisibility?: () => void
  /** @deprecated usar onResetVisibility */
  onReset?: () => void
  /** Callback para restaurar anchos a valores por defecto */
  onResetColumnSizing?: () => void
  /** Callback para restaurar todo (visibilidad + anchos) */
  onResetAll?: () => void
  /** Texto del botón */
  buttonText?: string
  /** Mostrar solo el icono (sin texto) */
  iconOnly?: boolean
}

/**
 * Obtiene el label de una columna para mostrar en el menú
 */
function getColumnLabel<TData>(column: Column<TData, unknown>): string {
  // Primero intentamos obtener el label desde meta
  const meta = column.columnDef.meta as { label?: string } | undefined
  if (meta?.label) {
    return meta.label
  }

  // Luego desde header si es string
  const header = column.columnDef.header
  if (typeof header === 'string') {
    return header
  }

  // Fallback al ID de la columna con formato legible
  return column.id
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim()
}

/**
 * Menú dropdown para controlar visibilidad y anchos de columnas
 *
 * @example
 * <ColumnVisibilityMenu
 *   table={table}
 *   onResetVisibility={resetVisibility}
 *   onResetColumnSizing={resetColumnSizing}
 *   onResetAll={resetAll}
 * />
 */
export function ColumnVisibilityMenu<TData>({
  table,
  onReset,
  onResetVisibility,
  onResetColumnSizing,
  onResetAll,
  buttonText = 'Columnas',
  iconOnly = false,
}: ColumnVisibilityMenuProps<TData>) {
  // Soporte para onReset legacy
  const handleResetVisibility = onResetVisibility || onReset

  // Solo columnas que pueden ocultarse
  const hideableColumns = table
    .getAllColumns()
    .filter((column) => column.getCanHide())

  if (hideableColumns.length === 0) {
    return null
  }

  // Contar columnas visibles
  const visibleCount = hideableColumns.filter((col) => col.getIsVisible()).length
  const totalCount = hideableColumns.length

  // Verificar si hay opciones de reset disponibles
  const hasResetOptions = handleResetVisibility || onResetColumnSizing || onResetAll

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8">
          <Settings2 className="h-4 w-4" />
          {!iconOnly && (
            <>
              <span className="ml-2">{buttonText}</span>
              <span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-xs font-medium">
                {visibleCount}/{totalCount}
              </span>
            </>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuLabel>Columnas visibles</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {hideableColumns.map((column) => (
          <DropdownMenuCheckboxItem
            key={column.id}
            checked={column.getIsVisible()}
            onCheckedChange={(value) => column.toggleVisibility(!!value)}
            onSelect={(e) => e.preventDefault()} // Evitar cerrar el menú
          >
            {getColumnLabel(column)}
          </DropdownMenuCheckboxItem>
        ))}

        {hasResetOptions && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
              Restablecer
            </DropdownMenuLabel>

            {handleResetVisibility && (
              <DropdownMenuItem onClick={handleResetVisibility}>
                <Columns className="mr-2 h-4 w-4" />
                Visibilidad
              </DropdownMenuItem>
            )}

            {onResetColumnSizing && (
              <DropdownMenuItem onClick={onResetColumnSizing}>
                <MoveHorizontal className="mr-2 h-4 w-4" />
                Anchos
              </DropdownMenuItem>
            )}

            {onResetAll && (
              <DropdownMenuItem onClick={onResetAll}>
                <RotateCcw className="mr-2 h-4 w-4" />
                Todo
              </DropdownMenuItem>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
